import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';

export interface CompanyProfile {
  whatTheyDo: string;
  howTheyMakeMoney: string;
  tailwinds: string[];
  headwinds: string[];
  companyDescription: string;
  logoUrl?: string;
}

export interface AnalysisSource {
  title: string;
  url: string;
}

export interface StockAnalysis {
  priceChart: string;
  businessQuality: {
    industryPosition: string;
    management: string;
    productsAndServices: string;
  };
  group1Analysis: string;
  group2Analysis: string;
  group3Analysis: string;
  group4Analysis: string;
  summary: {
    attractive: string[];
    caution: string[];
  };
  companyProfile?: CompanyProfile;
  sources?: AnalysisSource[];
}

interface AnalysisResponse {
  analysis: StockAnalysis;
  avatar: string;
  symbol: string;
  category: string;
  date: string;
  provider: string;
  timestamp: string;
}

interface UseAnalysisOptions {
  category: string;
  gameDate: string;
  avatarId?: string;
  enabled: boolean;
}

interface UseAnalysisReturn {
  analysis: StockAnalysis | null;
  isLoading: boolean;
  error: string | null;
  requiresPremium: boolean;
}

export const useAnalysis = ({
  category,
  gameDate,
  avatarId = 'baron-wuffett',
  enabled,
}: UseAnalysisOptions): UseAnalysisReturn => {
  const [analysis, setAnalysis] = useState<StockAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requiresPremium, setRequiresPremium] = useState(false);

  const fetchAnalysis = useCallback(async () => {
    if (!enabled || !category || !gameDate) return;

    setIsLoading(true);
    setError(null);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

      const params = new URLSearchParams();
      params.append('date', gameDate);
      params.append('avatar', avatarId);

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/${category}/analysis?${params.toString()}`,
        { headers }
      );

      if (response.status === 404) {
        setAnalysis(null);
        setError('not_found');
        return;
      }

      if (response.status === 401 || response.status === 403) {
        setAnalysis(null);
        setRequiresPremium(true);
        setError('premium_required');
        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch analysis: ${response.statusText}`);
      }

      const data: AnalysisResponse = await response.json();
      setAnalysis(data.analysis);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch analysis';
      console.error('Error fetching analysis:', err);
      setError(message);
      setAnalysis(null);
    } finally {
      setIsLoading(false);
    }
  }, [category, gameDate, avatarId, enabled]);

  useEffect(() => {
    fetchAnalysis();
  }, [fetchAnalysis]);

  return { analysis, isLoading, error, requiresPremium };
};
